import { useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Button } from "@mui/material";
import ConfirmDeleteModal from "../ConfirmDeleteModal";
import {
  cancelSubscription,
  getProfile,
  resumeSubscription,
} from "../../redux/asyncThunk/userActions";
import { userSlice } from "../../redux/user/userSlice";
import { syncPaymentWithServer } from "../../utils/syncPayment";
import {
  formatBusinessPlanLabel,
  formatStorageDashboardLabel,
  formatSubscriptionExpiry,
  formatTeamUsageLabel,
  getStorageUsagePercent,
  getSubscriptionAlert,
  getSubscriptionRenewalStatusText,
  getTeamUsageAlert,
  isSubscriptionCancelled,
  canManageSubscriptionRenewal,
} from "../../utils/userProfile";

const { addMessage } = userSlice;

function SyncPaymentButton({ syncing, onSync }) {
  return (
    <Button
      size="small"
      variant="contained"
      disabled={syncing}
      onClick={onSync}
      sx={{ textTransform: "none", fontWeight: 600 }}
    >
      {syncing ? "Проверяем…" : "Проверить оплату"}
    </Button>
  );
}

function ProfileSubscriptionNotice({ alert }) {
  if (!alert) return null;

  return (
    <div
      className={`app-profile-subscription__notice app-profile-subscription__notice--${alert.severity}`}
      role="status"
    >
      <strong className="app-profile-subscription__notice-title">{alert.title}</strong>
      <p className="app-profile-subscription__notice-message">{alert.message}</p>
      {alert.cta && alert.ctaPath ? (
        <Link to={alert.ctaPath} className="app-profile-inline-link">
          {alert.cta}
        </Link>
      ) : null}
    </div>
  );
}

function ProfileStorageRow({ storage }) {
  if (!storage) return null;

  const storageLabel = formatStorageDashboardLabel(storage);
  const storagePercent = getStorageUsagePercent(storage);

  return (
    <div className="app-profile-subscription__storage">
      <span className="app-profile-subscription__storage-label">Место для видео</span>
      <span className="app-profile-subscription__storage-text">{storageLabel}</span>
      <span
        className="app-profile-subscription__storage-meter"
        role="progressbar"
        aria-valuenow={storagePercent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={storageLabel}
      >
        <span
          className={`app-profile-subscription__storage-meter-fill${
            storagePercent >= 90 ? " app-profile-subscription__storage-meter-fill--warn" : ""
          }`}
          style={{ width: `${storagePercent}%` }}
        />
      </span>
    </div>
  );
}

export default function ProfileSubscriptionPanel({
  userId,
  businessSubscription,
  storage = null,
  teamUsage = null,
}) {
  const dispatch = useDispatch();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const canManage = canManageSubscriptionRenewal(businessSubscription);
  const cancelled = isSubscriptionCancelled(businessSubscription);
  const planLabel = formatBusinessPlanLabel(businessSubscription);
  const expiryLabel = formatSubscriptionExpiry(businessSubscription);
  const renewalStatus = getSubscriptionRenewalStatusText(businessSubscription);
  const teamLabel = formatTeamUsageLabel(teamUsage);
  const notice =
    getTeamUsageAlert(teamUsage) ?? getSubscriptionAlert(businessSubscription, null);

  const handleSyncPayment = async () => {
    if (!userId) return;

    setSyncing(true);

    try {
      const result = await syncPaymentWithServer();

      if (result.synced) {
        dispatch(
          addMessage({
            type: "info",
            message:
              result.message || "Оплата подтверждена, подписка активирована",
          }),
        );
        await dispatch(getProfile({ id: userId }));
        return;
      }

      dispatch(
        addMessage({
          type: "info",
          message:
            result.message ||
            "Платёж ещё обрабатывается. Подождите минуту и попробуйте снова.",
        }),
      );
    } catch {
      dispatch(
        addMessage({
          type: "error",
          message: "Не удалось проверить оплату. Попробуйте позже.",
        }),
      );
    } finally {
      setSyncing(false);
    }
  };

  if (!canManage && !expiryLabel) {
    return (
      <section className="app-profile-card app-profile-card--subscription">
        <h3 className="app-profile-card__title">Подписка</h3>
        <p className="app-profile-muted app-profile-muted--compact">
          Подписка не подключена.
        </p>
        <p className="app-profile-muted app-profile-muted--compact">
          Если вы уже оплатили в ЮMoney, но подписка не появилась — нажмите
          «Проверить оплату».
        </p>
        <ProfileSubscriptionNotice alert={notice} />
        <ProfileStorageRow storage={storage} />
        <div className="app-profile-subscription__actions">
          <SyncPaymentButton syncing={syncing} onSync={handleSyncPayment} />
          <Link to="/main/price" className="app-profile-inline-link">
            Выбрать тариф
          </Link>
        </div>
      </section>
    );
  }

  const handleCancel = async () => {
    setIsSubmitting(true);

    try {
      await dispatch(cancelSubscription({ userId })).unwrap();
      setConfirmOpen(false);
    } catch {
      // сообщение уходит в redux messages
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResume = async () => {
    setIsSubmitting(true);

    try {
      await dispatch(resumeSubscription({ userId })).unwrap();
    } catch {
      // сообщение уходит в redux messages
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="app-profile-card app-profile-card--subscription">
      <h3 className="app-profile-card__title">Подписка</h3>

      <div className="app-profile-subscription">
        <div className="app-profile-subscription__plan">{planLabel}</div>
        {renewalStatus ? (
          <p
            className={`app-profile-subscription__status${
              cancelled ? " app-profile-subscription__status--cancelled" : ""
            }`}
          >
            {renewalStatus}
          </p>
        ) : null}
        {teamLabel ? (
          <p className="app-profile-subscription__status">
            Места в тарифе: {teamLabel}
          </p>
        ) : null}

        <ProfileSubscriptionNotice alert={notice} />
        <ProfileStorageRow storage={storage} />

        {canManage ? (
          <div className="app-profile-subscription__actions">
            {cancelled ? (
              <Button
                size="small"
                variant="outlined"
                disabled={isSubmitting}
                onClick={handleResume}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Включить автопродление
              </Button>
            ) : (
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={isSubmitting}
                onClick={() => setConfirmOpen(true)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Отменить автопродление
              </Button>
            )}
            <Link to="/main/price" className="app-profile-inline-link">
              Тарифы
            </Link>
          </div>
        ) : null}
      </div>

      <ConfirmDeleteModal
        open={confirmOpen}
        title="Отменить автопродление?"
        message={`Доступ сохранится до ${expiryLabel ?? "конца оплаченного периода"}.`}
        detail="Со следующего периода мы не будем списывать оплату. Вы сможете снова включить автопродление или оплатить подписку вручную."
        confirmLabel="Отменить автопродление"
        cancelLabel="Оставить"
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleCancel}
      />
    </section>
  );
}
